import {rotateCartesian} from "../../../utils/MathUtils";

class TwoFingerHandler {
    constructor(
        portal,
        updateCamera,
        scaleOptions,
        localCameraRef,
        options = {}
    ) {
        this.portal = portal;
        this.updateCamera = updateCamera;
        this.scaleOptions = scaleOptions;
        this.localCamera = localCameraRef;

        this.panSpeedMultiplier = options.panSpeedMultiplier ?? 1;
        this.friction = options.friction ?? 0.95;

        this.animationFrame = null;

        this.initManualGestureHandling();
    }

    initManualGestureHandling() {
        const el = this.portal.current;
        if (!el) return;

        el.addEventListener("touchstart", this.onTouchStart, {passive: false});
        el.addEventListener("touchmove", this.onTouchMove, {passive: false});
        el.addEventListener("touchend", this.onTouchEnd, {passive: false});
        el.addEventListener("touchcancel", this.onTouchEnd, {passive: false});
    }

    onTouchStart = (e) => {
        if (e.touches.length === 2) {
            this.initTwoFingerGesture(e);
        }
    };

    // ⭐ Extracted initialization logic
    initTwoFingerGesture(e) {
        e.preventDefault();
        const [t1, t2] = [...e.touches];

        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;

        this.startDistance = Math.hypot(dx, dy);
        this.startCenter = this.getTouchMidpoint([...e.touches]);
        this.startPanCamera = {...this.localCamera()};
    }

    onTouchMove = (e) => {
        if (e.touches.length >= 2) {
            e.preventDefault();
        }

        if (e.touches.length === 2 && !this.startPanCamera) {
            this.initTwoFingerGesture(e); // start pinch+pan mid-gesture
        }

        if (e.touches.length === 2 && this.startPanCamera) {
            const [t1, t2] = [...e.touches];

            const PINCH_THRESHOLD = 0;
            const PAN_THRESHOLD = 10;

            const currentCenter = this.getTouchMidpoint([t1, t2]);

            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const currentDistance = Math.hypot(dx, dy);

            const zoomFactor = currentDistance / this.startDistance;
            const panDx =
                (currentCenter.x - this.startCenter.x) *
                this.panSpeedMultiplier;
            const panDy =
                (currentCenter.y - this.startCenter.y) *
                this.panSpeedMultiplier;

            const zoomChanged = Math.abs(zoomFactor - 1) > PINCH_THRESHOLD;
            const panChanged = Math.hypot(panDx, panDy) > PAN_THRESHOLD;

            const {rotate} = this.startPanCamera;
            let newZoom = this.startPanCamera.zoom;
            let newX = this.startPanCamera.x;
            let newY = this.startPanCamera.y;

            if (zoomChanged) {
                newZoom = this.startPanCamera.zoom * zoomFactor;
            }

            if (panChanged) {
                const {x, y} = rotateCartesian(panDx, panDy, -rotate);
                newX = this.startPanCamera.x - x / newZoom;
                newY = this.startPanCamera.y - y / newZoom;
            }

            this.updateCamera((cam) => ({
                ...cam,
                x: newX,
                y: newY,
                zoom: newZoom,
            }));
        }
    };

    onTouchEnd = () => {
        if (this.startPanCamera && this.portal.current.touches?.length !== 2) {
            this.startPanCamera = null;
        }

        this.startDistance = null;
        this.startCenter = null;
    };

    // -----------------------------
    // Utilities
    // -----------------------------
    getTouchMidpoint(pointers) {
        if (pointers.length < 2) return null;
        return {
            x: (pointers[0].clientX + pointers[1].clientX) / 2,
            y: (pointers[0].clientY + pointers[1].clientY) / 2,
        };
    }

    destroy() {
        const el = this.portal?.current;
        if (el) {
            el.removeEventListener("touchstart", this.onTouchStart);
            el.removeEventListener("touchmove", this.onTouchMove);
            el.removeEventListener("touchend", this.onTouchEnd);
            el.removeEventListener("touchcancel", this.onTouchEnd);
        }

        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }

        this.startPanCamera = null;
        this.startDistance = null;
        this.startCenter = null;
    }
}

export default TwoFingerHandler;
